import React from "react";
import { Box, Button, CircularProgress, Dialog, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Transition } from "../../UiComponents/Transition/Transition";
import { formatINR, paymentModeLabel } from "./addMoneyUtils";
import { navyButtonSx, outlinedButtonSx } from "./parts";
import Styles from "./AddMoneyToUser.module.scss";

const paperProps = { sx: { maxWidth: "480px !important", width: "100%", borderRadius: "10px" } };

const Row = ({ label, children }) => (
  <>
    <dt className={Styles.SummaryKey}>{label}</dt>
    <dd className={Styles.SummaryValue}>{children}</dd>
  </>
);

/** "Add ₹X to NAME's Policy Wallet?" — the last look before money moves. */
export const ConfirmAddMoneyDialog = ({ open, details, currentBalance, balanceAfter, posting, onCancel, onConfirm }) => {
  if (!details) return null;
  const amountText = formatINR(details.amount);
  return (
    <Dialog
      open={open}
      onClose={posting ? undefined : onCancel}
      disableEscapeKeyDown={posting}
      TransitionComponent={Transition}
      PaperProps={paperProps}
      aria-labelledby="add-money-confirm-title"
      aria-describedby="add-money-confirm-text"
    >
      <Box className={Styles.DialogHeader}>
        <Typography id="add-money-confirm-title" className={Styles.DialogTitle}>
          Confirm add money
        </Typography>
        <Typography className={Styles.DialogSubtitle}>
          Policy Wallet · {details.user.name}
          {details.user.adminName ? ` · Admin ${details.user.adminName}` : ""}
        </Typography>
      </Box>
      <Box className={Styles.DialogBody}>
        <Typography id="add-money-confirm-text" sx={{ fontFamily: "Inter", fontSize: "14px", color: "#101828" }}>
          Add <strong>{amountText}</strong> to <strong>{details.user.name}</strong>&apos;s Policy Wallet? This is
          credited immediately and cannot be undone.
        </Typography>
        <Box component="dl" className={Styles.SummaryRows} sx={{ m: 0 }}>
          <Row label="Payment mode">{paymentModeLabel(details.paymentMode)}</Row>
          {details.reference && <Row label="Reference">{details.reference}</Row>}
          {details.receipt && <Row label="Proof">{details.receipt.name}</Row>}
          <Row label="Balance">
            {formatINR(currentBalance)} &rarr; <strong>{formatINR(balanceAfter)}</strong>
          </Row>
        </Box>
      </Box>
      <Box className={Styles.DialogFooter}>
        <Button variant="outlined" autoFocus onClick={onCancel} disabled={posting} sx={outlinedButtonSx}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={onConfirm}
          disabled={posting}
          startIcon={
            posting ? (
              <CircularProgress size={14} sx={{ color: "inherit" }} />
            ) : (
              <AddIcon sx={{ fontSize: "16px !important" }} />
            )
          }
          sx={navyButtonSx}
        >
          {posting ? "Adding…" : `Add ${amountText}`}
        </Button>
      </Box>
    </Dialog>
  );
};

/** Shown after the server confirms a new credit. `result` is the addMoney response data. */
export const AddMoneySuccessDialog = ({ result, onClose }) => (
  <Dialog
    open={Boolean(result)}
    onClose={onClose}
    TransitionComponent={Transition}
    PaperProps={paperProps}
    aria-labelledby="add-money-success-title"
  >
    {result && (
      <>
        <Box className={Styles.DialogHeader}>
          <Typography id="add-money-success-title" className={Styles.DialogTitle}>
            Money added
          </Typography>
          <Typography className={Styles.DialogSubtitle}>Policy Wallet · {result.userName}</Typography>
        </Box>
        <Box className={Styles.DialogBody}>
          <Box className={Styles.SuccessBadge}>
            <CheckCircleOutlineIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography sx={{ fontFamily: "Inter", fontSize: "14px", color: "#101828", textAlign: "center" }}>
            <strong>{formatINR(result.amount)}</strong> was added to <strong>{result.userName}</strong>&apos;s Policy
            Wallet.
          </Typography>
          <Box component="dl" className={Styles.SummaryRows} sx={{ m: 0 }}>
            <Row label="New balance">
              <strong>{formatINR(result.balanceAfter)}</strong>
            </Row>
            <Row label="Payment mode">{paymentModeLabel(result.paymentMode)}</Row>
            {result.reference && <Row label="Reference">{result.reference}</Row>}
          </Box>
        </Box>
        <Box className={Styles.DialogFooter}>
          <Button variant="contained" disableElevation autoFocus onClick={onClose} sx={navyButtonSx}>
            Done
          </Button>
        </Box>
      </>
    )}
  </Dialog>
);
